import { z } from 'zod';
import { request, useMutation } from '@applyboard/ui-utils';
import { NewVasError } from '../common/errors/NewVasError';
import { getAccessToken } from '../common/utils/accessToken';

const CONTACT_VAS_SUPPORT_V1_URL = '/api/loans/v2/applications/contact_support';

const ContactVasSupportSchema = z.object({
  name: z.string(),
  email: z.string().email(),
  inquiry_details: z.string(),
});

export type ContactVasSupportType = z.infer<typeof ContactVasSupportSchema>;

export function useContactVasSupport() {
  const { mutate, mutateAsync, isLoading, isError } = useMutation(
    async (contactVasSupport: ContactVasSupportType) => {
      const token = await getAccessToken(false);
      return request(CONTACT_VAS_SUPPORT_V1_URL, {
        method: 'POST',
        authorization: token ? `Bearer ${token}` : undefined,
        schema: z.null(),
        body: contactVasSupport,
        throwError: async (response: Response) => {
          const jsonMessage = await response.json();
          throw new NewVasError(jsonMessage, 'Contact Vas support failed');
        },
      });
    },
  );

  const contactVasSupport = (
    contactVasSupport: ContactVasSupportType,
    onSuccess?: () => void,
    onError?: (error: unknown) => void,
  ) =>
    mutate(contactVasSupport, {
      onSuccess: () => onSuccess?.(),
      onError: (error: unknown) => onError?.(error),
    });

  const contactVasSupportAsync = async (contactVasSupport: ContactVasSupportType) =>
    mutateAsync(contactVasSupport);

  return {
    contactVasSupport,
    contactVasSupportAsync,
    isContactVasSupportLoading: isLoading,
    isContactVasSupportError: isError,
  };
}
